// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback } from 'react';
import { Action } from '../../../components/Table/types';
import { useParties } from '../../../contexts/PartiesContext';
import { useWithdrawTransferPreapproval } from '../../../hooks/mutations/registry/transferPreapproval/useWithdrawTransferPreapproval';
import useTransferPreapprovals from '../../../hooks/queries/registry/transferPreapproval/useTransferPreapprovals';
import type { TransferPreapprovalRow } from './createTransferPreapprovalColumns';

export function useTransferPreapprovalTable() {
  const { party } = useParties();
  const transferPreapprovals = useTransferPreapprovals();
  const withdrawPreapproval = useWithdrawTransferPreapproval();
  const [modifyContract, setModifyContract] = React.useState<TransferPreapprovalRow | null>(null);

  const actions = useCallback(
    (r: TransferPreapprovalRow, onModify?: (row: TransferPreapprovalRow) => void) => {
      const actionsTransferPreapproval: Action<TransferPreapprovalRow>[] = [];
      if (r.payload.receiver === party) {
        if (onModify) {
          actionsTransferPreapproval.push({
            color: 'primary',
            name: 'Modify',
            action: (row: TransferPreapprovalRow) => {
              onModify(row);
              return Promise.resolve();
            },
          });
        }
        actionsTransferPreapproval.push({
          color: 'error',
          name: 'Withdraw',
          action: (row: TransferPreapprovalRow) => {
            return withdrawPreapproval({
              cid: row.contractId,
              argument: { actor: party },
            });
          },
        });
      }
      return actionsTransferPreapproval;
    },
    [withdrawPreapproval, party],
  );

  const rows = React.useMemo(
    () =>
      transferPreapprovals.data?.map((r) => ({
        contractId: r.contractId,
        payload: r.payload,
        state: '',
        reason: undefined,
      })) || [],
    [transferPreapprovals.data],
  );

  return {
    rows,
    modifyContract,
    setModifyContract,
    actions,
    isLoading: transferPreapprovals.isLoading,
  };
}
